import { extractViewBox } from "./svgUtils";

/**
 * Inner markup between the root `<svg>` and `</svg>` (vector only).
 *
 * Skips a leading XML declaration (`<?xml …?>`) so the first `>` is not taken
 * from that header (which would leave a nested `<svg>` inside the host and
 * crop the artwork to the nested svg’s default viewport).
 */
export function extractSvgInnerContent(svgString: string): string {
    let s = svgString.trim();
    s = s.replace(/<\?xml[\s\S]*?\?>/i, "").trim();
    s = s.replace(/<!DOCTYPE[\s\S]*?>/i, "").trim();

    const svgOpen = s.search(/<svg\b/i);
    if (svgOpen === -1) {
        const openEnd = s.indexOf(">");
        if (openEnd === -1) return s;
        const inner = s.slice(openEnd + 1);
        const close = inner.lastIndexOf("</svg>");
        if (close === -1) return inner.trim();
        return inner.slice(0, close).trim();
    }
    const gt = s.indexOf(">", svgOpen);
    if (gt === -1) return s;
    const inner = s.slice(gt + 1);
    const close = inner.lastIndexOf("</svg>");
    if (close === -1) return inner.trim();
    return inner.slice(0, close).trim();
}

export function parseViewBoxParts(
    viewBoxAttr: string
): { x: number; y: number; width: number; height: number } | null {
    const parts = viewBoxAttr.trim().split(/[\s,]+/).map(Number);
    if (parts.length !== 4 || parts.some((n) => Number.isNaN(n))) return null;
    const [x, y, width, height] = parts;
    if (width <= 0 || height <= 0) return null;
    return { x, y, width, height };
}

function getSvgDimensionsFromString(svg: string): { width: number; height: number } | null {
    const extracted = extractViewBox(svg);
    if (extracted?.viewBox) {
        const [, , w, h] = extracted.viewBox.split(/\s+/).map(parseFloat);
        if (!Number.isNaN(w) && !Number.isNaN(h) && w > 0 && h > 0) {
            return { width: w, height: h };
        }
    }
    if (extracted?.width && extracted?.height) {
        return { width: extracted.width, height: extracted.height };
    }
    return null;
}

/** `viewBox` string for the preview/export SVG. */
export function viewBoxAttrFromSvg(svgString: string): string {
    const extracted = extractViewBox(svgString);
    if (extracted?.viewBox) return extracted.viewBox;
    const dims = getSvgDimensionsFromString(svgString);
    if (dims) return `0 0 ${dims.width} ${dims.height}`;
    return "0 0 100 100";
}

export type ChannelCompositeMode = "rgb-screen" | "cmyk-multiply";
export type ChannelDistortionMethod = "filter-displacement" | "path-displacement";

/** feColorMatrix type="matrix" — 20 values, row-major. */

export const RGB_CHANNEL_MATRICES: readonly string[] = [
    "1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0",
    "0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0",
    "0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0",
];

/** Simplified subtractive CMY + luminance “K” plate (vector approximation). */
export const CMYK_CHANNEL_MATRICES: readonly string[] = [
    "0 0 0 0 0  -1 0 0 0 255  -1 0 0 0 255  0 0 0 1 0",
    "-1 0 0 0 255  0 0 0 0 0  -1 0 0 0 255  0 0 0 1 0",
    "-1 0 0 0 255  -1 0 0 0 255  0 0 0 0 0  0 0 0 1 0",
    "0.2126 0.7152 0.0722 0 0  0.2126 0.7152 0.0722 0 0  0.2126 0.7152 0.0722 0 0  0 0 0 1 0",
];

export function channelCount(mode: ChannelCompositeMode): number {
    return mode === "rgb-screen" ? 3 : 4;
}

export function channelLabels(mode: ChannelCompositeMode): string[] {
    return mode === "rgb-screen"
        ? ["Red", "Green", "Blue"]
        : ["Cyan", "Magenta", "Yellow", "Black"];
}

export function matricesForMode(mode: ChannelCompositeMode): readonly string[] {
    return mode === "rgb-screen" ? RGB_CHANNEL_MATRICES : CMYK_CHANNEL_MATRICES;
}

interface Rgba {
    r: number;
    g: number;
    b: number;
    a: number;
}

let colorParseCtx: CanvasRenderingContext2D | null = null;

function parseColorToRgba(input: string): Rgba | null {
    if (typeof document === "undefined") return null;
    if (!colorParseCtx) {
        const c = document.createElement("canvas");
        c.width = 1;
        c.height = 1;
        colorParseCtx = c.getContext("2d");
    }
    if (!colorParseCtx) return null;
    const ctx = colorParseCtx;
    const raw = input.trim();
    if (!raw || raw === "none" || raw.startsWith("url(") || raw === "currentColor") {
        return null;
    }
    ctx.fillStyle = "#000000";
    ctx.fillStyle = raw;
    const normalized = String(ctx.fillStyle).trim();
    const m = normalized.match(/^#([0-9a-f]{6}|[0-9a-f]{8})$/i);
    if (!m) return null;
    const hex = m[1];
    const hasAlpha = hex.length === 8;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const a = hasAlpha ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
    return { r, g, b, a };
}

function toRgbaString(c: Rgba): string {
    const a = Math.max(0, Math.min(1, c.a));
    return `rgba(${Math.round(c.r)},${Math.round(c.g)},${Math.round(c.b)},${a.toFixed(4)})`;
}

function remapColorForChannel(
    color: Rgba,
    mode: ChannelCompositeMode,
    channelIndex: number
): Rgba {
    const rn = color.r / 255;
    const gn = color.g / 255;
    const bn = color.b / 255;
    if (mode === "rgb-screen") {
        if (channelIndex === 0) return { r: 255, g: 0, b: 0, a: color.a * rn };
        if (channelIndex === 1) return { r: 0, g: 255, b: 0, a: color.a * gn };
        return { r: 0, g: 0, b: 255, a: color.a * bn };
    }
    const c = 1 - rn;
    const m = 1 - gn;
    const y = 1 - bn;
    const k = Math.min(c, m, y);
    if (channelIndex === 0) return { r: 0, g: 255, b: 255, a: color.a * c };
    if (channelIndex === 1) return { r: 255, g: 0, b: 255, a: color.a * m };
    if (channelIndex === 2) return { r: 255, g: 255, b: 0, a: color.a * y };
    return { r: 0, g: 0, b: 0, a: color.a * k };
}

function remapPaintValue(
    value: string,
    mode: ChannelCompositeMode,
    channelIndex: number
): string {
    const parsed = parseColorToRgba(value);
    if (!parsed) return value;
    return toRgbaString(remapColorForChannel(parsed, mode, channelIndex));
}

export function recolorSvgMarkupForChannel(
    markup: string,
    mode: ChannelCompositeMode,
    channelIndex: number
): string {
    const withAttrs = markup.replace(
        /(\s(?:fill|stroke)=["'])([^"']*)(["'])/gi,
        (_m, p1: string, val: string, p3: string) =>
            `${p1}${remapPaintValue(val, mode, channelIndex)}${p3}`
    );
    return withAttrs.replace(/(style=["'])([^"']*)(["'])/gi, (_m, p1: string, style: string, p3: string) => {
        const updated = style.replace(
            /((?:^|;)\s*(?:fill|stroke)\s*:\s*)([^;]+)/gi,
            (_sm, prefix: string, val: string) => `${prefix}${remapPaintValue(val, mode, channelIndex)}`
        );
        return `${p1}${updated}${p3}`;
    });
}

const TURBULENCE_BASE = "0.045";
const TURBULENCE_OCTAVES = "2";

export interface ChannelSplitSvgBuildParams {
    viewBoxAttr: string;
    vb: { x: number; y: number; width: number; height: number };
    symbolId: string;
    filterIdPrefix: string;
    innerMarkup: string;
    mode: ChannelCompositeMode;
    roughenScales: number[];
    xOffsets?: number[];
    yOffsets?: number[];
    /** Degrees; rotation is around the viewBox center, then X/Y translate is applied. */
    rotationsDeg?: number[];
    channelVisible?: boolean[];
    layerMarkups?: string[];
    distortionMethod?: ChannelDistortionMethod;
}

function filterRegionAttrs(
    vb: { x: number; y: number; width: number; height: number },
    roughenScale: number,
): string {
    const padBase = Math.max(vb.width, vb.height) * 0.35;
    const padDisp = Math.max(0, roughenScale) * 1.5;
    const pad = padBase + padDisp;
    const x = vb.x - pad;
    const y = vb.y - pad;
    const w = vb.width + pad * 2;
    const h = vb.height + pad * 2;
    return `filterUnits="userSpaceOnUse" x="${x}" y="${y}" width="${w}" height="${h}"`;
}

function buildChannelLayerTransformAttr(
    vb: { x: number; y: number; width: number; height: number },
    dx: number,
    dy: number,
    rotateDeg: number,
): string {
    const cx = vb.x + vb.width / 2;
    const cy = vb.y + vb.height / 2;
    const hasTranslate = dx !== 0 || dy !== 0;
    const hasRotate = Number.isFinite(rotateDeg) && rotateDeg !== 0;
    if (!hasTranslate && !hasRotate) return "";
    let inner: string;
    if (hasRotate && hasTranslate) {
        inner = `translate(${dx} ${dy}) translate(${cx} ${cy}) rotate(${rotateDeg}) translate(${-cx} ${-cy})`;
    } else if (hasRotate) {
        inner = `translate(${cx} ${cy}) rotate(${rotateDeg}) translate(${-cx} ${-cy})`;
    } else {
        inner = `translate(${dx} ${dy})`;
    }
    return ` transform="${inner}"`;
}

function buildSingleFilterXml(
    filterId: string,
    matrixValues: string,
    roughenScale: number,
    turbulenceSeed: number,
    vb: { x: number; y: number; width: number; height: number }
): string {
    const region = filterRegionAttrs(vb, roughenScale);
    const colorMatrix = `<feColorMatrix in="SourceGraphic" type="matrix" values="${matrixValues}" result="chan"/>`;
    if (roughenScale <= 0) {
        return `<filter id="${filterId}" ${region} color-interpolation-filters="sRGB">${colorMatrix}</filter>`;
    }
    return `<filter id="${filterId}" ${region} color-interpolation-filters="sRGB">${colorMatrix}<feTurbulence type="fractalNoise" baseFrequency="${TURBULENCE_BASE}" numOctaves="${TURBULENCE_OCTAVES}" seed="${turbulenceSeed}" result="noise"/><feDisplacementMap in="chan" in2="noise" scale="${roughenScale}" xChannelSelector="R" yChannelSelector="G"/></filter>`;
}

/** Complete SVG document (vector layers only, no raster). */
export function buildChannelSplitSvgDocument(p: ChannelSplitSvgBuildParams): string {
    const matrices = matricesForMode(p.mode);
    const n = matrices.length;
    const scales = p.roughenScales.slice(0, n);
    while (scales.length < n) scales.push(0);
    const xOffsets = p.xOffsets?.slice(0, n) ?? [];
    const yOffsets = p.yOffsets?.slice(0, n) ?? [];
    const rotationsDeg = p.rotationsDeg?.slice(0, n) ?? [];
    const channelVisible = p.channelVisible?.slice(0, n) ?? [];
    while (xOffsets.length < n) xOffsets.push(0);
    while (yOffsets.length < n) yOffsets.push(0);
    while (rotationsDeg.length < n) rotationsDeg.push(0);
    while (channelVisible.length < n) channelVisible.push(true);

    const shouldUseFilters = p.distortionMethod !== "path-displacement";
    const filters: string[] = [];
    if (shouldUseFilters) {
        for (let i = 0; i < n; i++) {
            const fid = `${p.filterIdPrefix}-f-${i}`;
            const seed = 17 + i * 47;
            filters.push(buildSingleFilterXml(fid, matrices[i], scales[i] ?? 0, seed, p.vb));
        }
    }

    const blend = p.mode === "rgb-screen" ? "screen" : "multiply";
    const bg =
        p.mode === "rgb-screen"
            ? `<rect x="${p.vb.x}" y="${p.vb.y}" width="${p.vb.width}" height="${p.vb.height}" fill="#000000"/>`
            : `<rect x="${p.vb.x}" y="${p.vb.y}" width="${p.vb.width}" height="${p.vb.height}" fill="#ffffff"/>`;

    const uses: string[] = [];
    for (let i = 0; i < n; i++) {
        if (!channelVisible[i]) continue;
        const dx = xOffsets[i] ?? 0;
        const dy = yOffsets[i] ?? 0;
        const rot = rotationsDeg[i] ?? 0;
        const transformAttr = buildChannelLayerTransformAttr(p.vb, dx, dy, rot);
        const layerMarkup = p.layerMarkups?.[i];
        if (layerMarkup) {
            if (shouldUseFilters) {
                uses.push(
                    `<g filter="url(#${p.filterIdPrefix}-f-${i})" style="mix-blend-mode: ${blend}"${transformAttr}>${layerMarkup}</g>`
                );
            } else {
                uses.push(
                    `<g style="mix-blend-mode: ${blend}"${transformAttr}>${layerMarkup}</g>`
                );
            }
        } else {
            const useGeom = `x="${p.vb.x}" y="${p.vb.y}" width="${p.vb.width}" height="${p.vb.height}"`;
            if (shouldUseFilters) {
                uses.push(
                    `<use href="#${p.symbolId}" ${useGeom}${transformAttr} filter="url(#${p.filterIdPrefix}-f-${i})" style="mix-blend-mode: ${blend}"/>`
                );
            } else {
                uses.push(
                    `<use href="#${p.symbolId}" ${useGeom}${transformAttr} style="mix-blend-mode: ${blend}"/>`
                );
            }
        }
    }

    const needsSymbol = !p.layerMarkups || p.layerMarkups.length === 0;
    const defsParts: string[] = [];
    if (needsSymbol) {
        defsParts.push(
            `<symbol id="${p.symbolId}" viewBox="${p.viewBoxAttr}" overflow="visible">${p.innerMarkup}</symbol>`,
        );
    }
    if (filters.length > 0) defsParts.push(filters.join(""));
    const defs = defsParts.length > 0 ? `<defs>${defsParts.join("")}</defs>` : "";
    const body = `${bg}${uses.join("")}`;

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${p.viewBoxAttr}" width="${p.vb.width}" height="${p.vb.height}" overflow="visible">${defs}${body}</svg>`;
}
